import { useState, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useApartments,
  useDeleteApartment,
  usePublishApartment,
  useUnpublishApartment,
  useRankApartmentUp,
  useRankApartmentDown,
  useRankApartmentToTop,
} from '../hooks/useApartments';
import { ApartmentCard } from '../components/ApartmentCard';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ListRefreshButton } from '../components/ListRefreshButton';
import { is404, is422 } from '../utils/errors';
import { isAuthRequiredError } from '../utils/listRefreshState';

export const Apartments = () => {
  const { t } = useTranslation(['common', 'pages', 'errors', 'forms', 'badges', 'navigation']);
  const navigate = useNavigate();
  const [actionError, setActionError] = useState<{ id: string; message: string } | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  // ── Confirm dialog state ───────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    message: string;
    onConfirm: () => void;
  }>({ open: false, message: '', onConfirm: () => void 0 });

  const { data: apartments, isLoading, isFetching, error, refetch } = useApartments();
  const deleteMutation = useDeleteApartment();
  const publishMutation = usePublishApartment();
  const unpublishMutation = useUnpublishApartment();
  const rankUpMutation = useRankApartmentUp();
  const rankDownMutation = useRankApartmentDown();
  const rankToTopMutation = useRankApartmentToTop();

  // Sort apartments by viewOrder ascending for consistent display and boundary detection
  const sortedApartments = useMemo(
    () => [...(apartments ?? [])].sort((a, b) => a.viewOrder - b.viewOrder),
    [apartments]
  );
  const minViewOrder = sortedApartments[0]?.viewOrder ?? 0;
  const maxViewOrder = sortedApartments[sortedApartments.length - 1]?.viewOrder ?? 0;
  const isAnyRankingPending =
    rankUpMutation.isPending || rankDownMutation.isPending || rankToTopMutation.isPending;

  const handlePublish = async (id: string) => {
    setActionError(null);
    try {
      await publishMutation.mutateAsync(id);
    } catch (err) {
      setActionError({
        id,
        message: is422(err)
          ? t('errors:publishBlockedNoGeocode')
          : is404(err)
          ? t('errors:apartmentNotFound')
          : t('errors:somethingWentWrong'),
      });
    }
  };

  const handleUnpublish = async (id: string) => {
    setActionError(null);
    try {
      await unpublishMutation.mutateAsync(id);
    } catch (err) {
      setActionError({
        id,
        message: is404(err)
          ? t('errors:apartmentNotFound')
          : t('errors:somethingWentWrong'),
      });
    }
  };

  const handleDelete = (id: string) => {
    setConfirmDialog({
      open: true,
      message: t('pages:deleteApartmentConfirm'),
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        try {
          await deleteMutation.mutateAsync(id);
        } catch {
          setActionError({ id, message: t('errors:failedToDeleteApartment') });
        }
      },
    });
  };

  const handleRefresh = async () => {
    try {
      setRefreshError(null);
      await refetch({ throwOnError: true });
    } catch (err) {
      setRefreshError(isAuthRequiredError(err) ? t('common:signInRequired') : t('common:refreshFailed'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">{t('pages:loadingApartments')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-red-50 border-red-200">
        <p className="text-red-600 text-center">
          {t('errors:failedToLoadApartments')}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('pages:apartmentsHeading')}</h1>
          <p className="text-gray-600 mt-2">{t('pages:apartmentsSubHeading')}</p>
        </div>
        <div className="flex items-center gap-3">
          <ListRefreshButton
            isRefreshing={isFetching}
            onRefresh={handleRefresh}
            className="flex items-center gap-2 py-2 px-3 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button onClick={() => navigate('/apartments/new')} className="btn-primary flex items-center gap-2">
            <Plus size={20} />
            {t('pages:addApartment')}
          </button>
        </div>
      </div>

      {refreshError && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {refreshError}
        </div>
      )}

      {apartments && apartments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedApartments.map((apartment) => (
            <ApartmentCard
              key={apartment.id}
              apartment={apartment}
              isFirst={apartment.viewOrder === minViewOrder}
              isLast={apartment.viewOrder === maxViewOrder}
              isRankingDisabled={isAnyRankingPending}
              publishPending={publishMutation.isPending}
              unpublishPending={unpublishMutation.isPending}
              deletePending={deleteMutation.isPending}
              actionError={actionError?.id === apartment.id ? actionError.message : null}
              onDelete={handleDelete}
              onPublish={handlePublish}
              onUnpublish={handleUnpublish}
              onRankError={(message) => setActionError({ id: apartment.id, message })}
            />
          ))}
        </div>
      ) : (
        <div className="card">
          <p className="text-gray-600 text-center py-12">
            {t('pages:noApartmentsYet')}
          </p>
        </div>
      )}

      {/* Confirm dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        message={confirmDialog.message}
        intent="danger"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(d => ({ ...d, open: false }))}
      />
    </div>
  );
};

export default Apartments;
